import { Version } from "@/domain/Version";

const escapeRegExp = (value: string) =>
  value.replace(/[\\^$.*+?()[\]{}|]/g, "\\$&");

/**
 * バージョンを複製・最適化する際に、元のバージョン名から新規作成するバージョンのデフォルト名を作成する関数
 * 重複チェックは同一カテゴリ内のバージョンのみを対象とする (関数内部では title と category のみ参照)
 * @param title 元のバージョン名
 * @param versions すでにあるバージョン名リスト
 * @param category 重複チェック対象とするカテゴリ (元のバージョンが属するカテゴリ)
 * @param suffix 付与する接尾辞。デフォルトは"_"。例: "_copy", "_optimize"
 * @returns 新規作成するバージョン名
 */
export function createDefaultVersionTitle({
  title,
  versions,
  category,
  suffix = "_",
}: {
  title: string;
  versions: Version[];
  category: string;
  suffix?: string;
}): string {
  // すでに"_(数字3桁)"で終わっている場合はその部分を除去したものをベースのタイトルとする
  const regex_base = new RegExp(`^(.*)${suffix}\\d+$`);
  const match = title.match(regex_base);
  const baseTitle = match ? match[1] : title;

  // 同一カテゴリ内の既存バージョンから番号を抽出して最大値を取得し、+1したものを新しい番号とする
  const regex = new RegExp(`^${escapeRegExp(baseTitle)}${suffix}(\\d+)$`);
  const numberingList = versions
    .filter((v) => v.category === category && regex.test(v.title))
    .map((v) => v.title.match(regex)?.[1])
    .map((v) => (v ? Number(v) : 0));
  const maxNumbering =
    numberingList.length > 0 ? Math.max(...numberingList) : 0;

  return `${baseTitle}${suffix}${(maxNumbering + 1).toString().padStart(3, "0")}`;
}

/**
 * 新規作成するバージョン名のvalidateを行う関数
 * 重複チェックは同一カテゴリ内のバージョンのみを対象とする
 * @param newVersionTitle 新規作成するバージョン名
 * @param versions すでにあるバージョン名リスト
 * @param category 重複チェック対象とするカテゴリ
 * @returns validなバージョン名かどうかと、入力欄下に表示するメッセージを返す
 */
export function validateNewVersionTitle(
  newVersionTitle: string,
  versions: Version[],
  category: string
): {
  valid: boolean;
  message?: string;
} {
  // error: 操作不可の場合
  if (!newVersionTitle.trim())
    return {
      valid: false,
      message: "必須項目です",
    };
  // warn: 操作有効だが、アラートメッセージを表示したい場合
  if (
    versions.some((v) => v.category === category && v.title === newVersionTitle)
  )
    return {
      valid: true,
      message: "同じ名前のバージョンがあります",
    };
  // ok: 有効な場合
  return { valid: true };
}
